import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import DocumentUploadModal from "./DocumentUploadModal";
import AddActualsModal from "./ActualsModal";
import StopReasonModal from "./StopReasonModal";
import RatesShoppingModal from "./RatesShoppingModal";
import IntermodalRatesShoppingModal from "./IntermodalRatesShoppingModal";
import ConfirmRecallModal from "./ConfirmRecallModal";
import ReorderStopsModal from "./ReorderStopsModal";
import AddStopModal from "./AddStopModal";
import ReceiverCarrierSelectionModal from "./ReceiverCarrierSelectionModal";
const RenderShipmentModals = ({
  showDocUploadModal,
  setShowDocUploadModal,
  showActualsModal,
  setShowActualsModal,
  showStopReasonModal,
  setShowStopReasonModal,
  showModal,
  setShowModal,
  showRecallModal,
  setShowRecallModal,
  shipmentDetails,
  shipmentNumber,
  fetchShipmentDetails,
  handleUploadSuccess,
  onStopReasonAdded,
  handleRecallConfirm,
  showReorderModal,
  setShowReorderModal,
  showAddStopModal,
  setShowAddStopModal,
  handleStopAdded,
  showReceiverCarrierModal,
  setShowReceiverCarrierModal,
  receiverCarriers = [],
  initialReceiverCarrier = null,
  showCancelShipmentModal,
  setShowCancelShipmentModal,
  showRemoveOrdersModal,
  setShowRemoveOrdersModal,
  handleCancelConfirm,
  showRejectModal,
  setShowRejectModal,
  handleRemoveOrdersConfirm,
  handleRejectConfirm,
  availableStops,
  rateShoppingShipmentNumber,
  onCarrierAssignmentComplete,
}) => {

  const isIntermodal = shipmentDetails?.movementType === "INTERMODAL";

  // Exclude EMPTY_PICKUP/EMPTY_RETURN stops — they have no scheduled times and wrong
  // addresses for origin/destination resolution in rate shopping.
  const EXCLUDED_STOP_TYPES = new Set(["EMPTY_PICKUP", "EMPTY_RETURN"]);
  const routingStops = [...(shipmentDetails.shipmentStops || [])]
    .filter((s) => !EXCLUDED_STOP_TYPES.has(s.stopType))
    .sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0));
  const sortedStops =
    routingStops.length > 0
      ? routingStops
      : [...(shipmentDetails.shipmentStops || [])].sort(
          (a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0)
        );

  const originStop = sortedStops[0];
  const destinationStop = sortedStops[sortedStops.length - 1];

  const originAddress = originStop?.addressLine1 +
      (originStop?.addressLine2 || '') + ", " +
      originStop?.city + ", " +
      originStop?.state + ", " +
      originStop?.zipCode + ", " +
      originStop?.country;

  const destinationAddress = destinationStop?.addressLine1 +
      (destinationStop?.addressLine2 || '') + ", " +
      destinationStop?.city + ", " +
      destinationStop?.state + ", " +
      destinationStop?.zipCode + ", " +
      destinationStop?.country;

  const formatDate = (dateString) => {
    if (!dateString) {
      const today = new Date();
      return `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      const today = new Date();
      return `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
    }
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  return (
    <>
      {showDocUploadModal && (
        <DocumentUploadModal
          onClose={() => setShowDocUploadModal(false)}
          shipmentNumber={shipmentNumber}
          onUploadSuccess={handleUploadSuccess}
          shipmentOfficeCode={shipmentDetails.officeCode}
          movementType={shipmentDetails.movementType}
        />
      )}

{showActualsModal?.open && shipmentDetails && (
  <AddActualsModal
    onClose={() => setShowActualsModal({ open: false, mode: null })}
    shipmentDetails={shipmentDetails}
    shipmentNumber={shipmentNumber}
    fetchShipmentDetails={fetchShipmentDetails}
    mode={showActualsModal.mode}   // 👈 pass the mode!
    paymentType={shipmentDetails?.paymentMethod}
    availableStops={availableStops}
  />
)}



{showReceiverCarrierModal && (
  <ReceiverCarrierSelectionModal
    onClose={() => setShowReceiverCarrierModal(false)}
    onConfirm={(carrier) => {
      
            setShowReceiverCarrierModal(false);
    }}
    shipmentNumber={shipmentNumber}
    receiverOfficeCode={shipmentDetails.receiverOfficeCode?.partyOfficeCode}
    officeCode={shipmentDetails.officeCode}
    // Temporary mapping: name -> description so the modal highlights correctly
    initiallySelectedCarrier={
      shipmentDetails.carrierOfficeCode != null && shipmentDetails.carrierOfficeCode.partyOfficeCode != null
        ? { code: shipmentDetails.carrierOfficeCode.partyOfficeCode, description: shipmentDetails.carrierOfficeCode.name }
        : null
    }
    // (Optional) clearer copy, since this is a carrier picker
    title="Receiver carrier selection"
    subtitle="Choose the Carrier that is going to ship to the receiver from the list below."
  />
)}




      {showStopReasonModal && shipmentDetails && (
        <StopReasonModal
          onClose={() => setShowStopReasonModal(false)}
          shipmentDetails={shipmentDetails}
          shipmentNumber={shipmentNumber}
          fetchShipmentDetails={fetchShipmentDetails}
          isShipper={shipmentDetails.status === "Accepted"}
          isPickup={shipmentDetails.status === "Ready for Pickup" || shipmentDetails.status === "Accepted"}
          isDropoff={shipmentDetails.status === "Active" || shipmentDetails.status === "In Transit"}
          onStopReasonAdded={onStopReasonAdded}
        />
      )}

    <AnimatePresence mode='wait'>
    {showModal && shipmentDetails && (
        <motion.div
        key="rates-shopping-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000]"
        >
        {isIntermodal ? (
          <IntermodalRatesShoppingModal
            showModal={showModal}
            onClose={() => setShowModal(false)}
            origin={originStop?.stopName || "Origin"}
            destination={destinationStop?.stopName || "Destination"}
            originAddress={originAddress}
            destinationAddress={destinationAddress}
            pickupDate={formatDate(shipmentDetails?.pickupDate)}
            originType="GEOGRAPHY"
            destinationType="GEOGRAPHY"
            strategy="ALL"
            totalMiles={shipmentDetails?.totalMiles}
            totalMinutes={
              shipmentDetails?.totalSeconds != null
                ? shipmentDetails.totalSeconds / 60
                : undefined
            }
            shipmentId={shipmentDetails.id}
            shipmentNumber={rateShoppingShipmentNumber || shipmentNumber}
            fetchShipmentDetails={fetchShipmentDetails}
            stops={sortedStops}
            shipmentDetails={shipmentDetails}
            officeCode={shipmentDetails.officeCode}
            onAssignmentComplete={onCarrierAssignmentComplete}
          />
        ) : (
          <RatesShoppingModal
            showModal={showModal}
            onClose={() => setShowModal(false)}
            origin={shipmentDetails?.shipmentStops[0]?.stopName || "Origin"}
            destination={shipmentDetails?.shipmentStops[shipmentDetails?.shipmentStops.length - 1]?.stopName || "Destination"}
            originAddress={originAddress}
            destinationAddress={destinationAddress}
            pickupDate={formatDate(shipmentDetails?.pickupDate)}
            originType="PORT"
            destinationType="PORT"
            strategy="ALL"
            distanceInKm={shipmentDetails?.totalKilometers}
            durationInMin={
              shipmentDetails?.totalSeconds != null
                ? shipmentDetails.totalSeconds / 60
                : undefined
            }
            totalMiles={shipmentDetails?.totalMiles}
            totalMinutes={
              shipmentDetails?.totalSeconds != null
                ? shipmentDetails.totalSeconds / 60
                : undefined
            }
            shipmentId={shipmentDetails.id}
            shipmentNumber={rateShoppingShipmentNumber || shipmentNumber}
            fetchShipmentDetails={fetchShipmentDetails}
            stops={shipmentDetails.shipmentStops}
            shipmentDetails={shipmentDetails}
            officeCode={shipmentDetails.officeCode}
            onAssignmentComplete={onCarrierAssignmentComplete}
          />
        )}
        </motion.div>
    )}
    </AnimatePresence>

      {showRecallModal && shipmentDetails && (
        <ConfirmRecallModal
          key={shipmentDetails.shipmentNumber}
          onClose={() => setShowRecallModal(false)}
          shipmentDetails={shipmentDetails}
          onConfirm={(reason) => {
            if (handleRecallConfirm) {
              handleRecallConfirm(reason); // Your handler like handleReject()
            }
            setShowRecallModal(false);
          }}
          title={"Recall shipment?"}
          warningMessage={"This shipment will be recalled. This shipment is no longer available to the carrier."}
          confirmButtonMessage={"Yes, untender the shipment"}
          requireReason={true}
        />
      )}

      {showCancelShipmentModal && shipmentDetails && (
        <ConfirmRecallModal
            key={shipmentDetails.shipmentNumber}
            onClose={() => setShowCancelShipmentModal(false)}
            shipmentDetails={shipmentDetails}
            onConfirm={() => {
              handleCancelConfirm?.();
              setShowCancelShipmentModal(false);
            }}
            title="Cancel shipment?"
            warningMessage={
              "This will permanently cancel the shipment. It will no longer be available for tendering, assignment, or edits." +
              " If you only need to adjust contents, consider removing any associated order instead."
            }
            confirmButtonMessage="Cancel Shipment"
            requireReason={false}
            isRemove={false}
          />
      )}

      {showRemoveOrdersModal && shipmentDetails && (
        <ConfirmRecallModal
          key={shipmentDetails.shipmentNumber}
          onClose={() => setShowRemoveOrdersModal(false)}
          shipmentDetails={shipmentDetails}
          onConfirm={(selectedSOs, selectedPOs) => {
            const soCsv = selectedSOs.join(",");
            const poCsv = selectedPOs.join(",");
            handleRemoveOrdersConfirm?.(soCsv, poCsv);
          setShowRemoveOrdersModal(false);
          }}
          title="Select Orders to Remove"
          warningMessage={
            <>
              <div>You’re about to remove one or more orders from this shipment.</div>
              <ul className="list-disc pl-6 mt-1">
                <li>The shipment will be recalled to <b>Unassigned</b>.</li>
                <li>At least one order must remain on the shipment.</li>
              </ul>
            </>
          }
          confirmButtonMessage="Remove Selected Orders"
          requireReason={false}
          isRemove={true}
        />
    )}


      {showRejectModal && shipmentDetails && (
        <ConfirmRecallModal
          key={`reject-${shipmentDetails.shipmentNumber}`}
          onClose={() => setShowRejectModal(false)}
          shipmentDetails={shipmentDetails}
          onConfirm={async (reason) => {
            await handleRejectConfirm?.(reason);
            setShowRejectModal(false);
          }}
          title="Reject shipment?"
          warningMessage="This shipment will be rejected and returned to the shipper. A reason is required."
          confirmButtonMessage="Yes, reject the shipment"
          requireReason={true}
        />
  )}


    <AnimatePresence mode='wait'>
    {showReorderModal && shipmentDetails && (
      <ReorderStopsModal
        key="reorder-stops-modal"
        onClose={() => {
          setShowReorderModal(false);
        }}
        shipmentNumber={shipmentNumber}
        stops={[...(shipmentDetails.shipmentStops || [])].sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0))}
        onConfirm={() => {
          setShowReorderModal(false);
        }}
      />
    )}
    </AnimatePresence>

    {showAddStopModal && (
      <AddStopModal
        onClose={() => setShowAddStopModal(false)}
        onAddStop={handleStopAdded}
      />
    )}
    </>
  );
};

export default RenderShipmentModals;